import {
  ApartmentOutlined,
  CarOutlined,
  GiftOutlined,
  MinusCircleOutlined,
  PercentageOutlined,
  PlusCircleOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Form } from 'antd';
import {
  CurrencySelect,
  Group,
  SettingSwitch,
} from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import VariationTypesForm from '../../VariationTypesForm';
import { useCompanyGeneralDataForm } from '../CompanyGeneralDataFormContext';

/**
 * The "Bonus and deductions" tile of the company profile as setting cards.
 * Same fields, visibility rules and save as CompanyBonusFields, which steps
 * aside on the profile.
 */
export default function CompanyBonusProfilePanel() {
  const ctx = useCompanyGeneralDataForm();
  const { currencyOptions, form, readOnly, sectionClass, t } = ctx;
  const f = (key) => t(key, { ns: 'forms' });
  const lock = (state) => readOnly || !!state?.disabled;
  const state = (name) => ctx[`${name}State`];
  const keys = (name) => ctx[`${name}Keys`];

  const currencyRow = (name, label) =>
    state(`${name}Currency`)?.visible && (
      <div className="flex items-center gap-2 text-[12px] text-zinc-500">
        {f(label)}
        <Form.Item name={keys(`${name}Currency`)} noStyle>
          <CurrencySelect
            options={currencyOptions}
            locked={lock(state(`${name}Currency`))}
          />
        </Form.Item>
      </div>
    );

  // The variations switch and, while it is on, the list of variation types.
  const variations = (name, title, listTitle) => {
    const enabledState = state(`${name}VariationsEnabled`);
    if (!enabledState?.visible) return null;
    return (
      <>
        <Form.Item name={keys(`${name}VariationsEnabled`)} noStyle>
          <SettingSwitch
            locked={lock(enabledState)}
            icon={<ApartmentOutlined />}
            title={f(title)}
          />
        </Form.Item>
        {ctx[`${name}VariationsEnabled`] && (
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="mb-2 text-[12px] font-semibold text-zinc-700">
              {f(listTitle)}
            </p>
            <VariationTypesForm
              name={keys(`${name}VariationsTypes`)}
              form={form}
              disabled={lock(enabledState)}
              showIncludeInDefault={!enabledState?.disabled}
            />
          </div>
        )}
      </>
    );
  };

  // One kind of payment: its switch card, with currency and variations
  // inside while it is on (and the card then takes the full width).
  const kind = (name, { icon, title, currency, variation }) => {
    if (!state(name)?.visible) return null;
    const on = !!ctx[`${name}Checked`];
    const inside = on && (
      <div className="grid gap-2 pl-[42px]">
        {currencyRow(name, currency)}
        {variation && variations(name, ...variation)}
      </div>
    );
    return (
      <Form.Item name={keys(name)} noStyle>
        <SettingSwitch
          locked={lock(state(name))}
          icon={icon}
          title={f(title)}
          wide={on}
        >
          {inside}
        </SettingSwitch>
      </Form.Item>
    );
  };

  return (
    <div className={cn('grid gap-4', sectionClass('bonus'))}>
      <Group>
        {kind('bonus', {
          icon: <TrophyOutlined />,
          title: 'bonus',
          currency: 'bonusCurrency',
          variation: ['bonusVariationsEnabled', 'bonusVariations'],
        })}
        {state('isGrossBonus')?.visible && (
          <Form.Item name={keys('isGrossBonus')} noStyle>
            <SettingSwitch
              locked={lock(state('isGrossBonus'))}
              icon={<PercentageOutlined />}
              title={f('grossBonus')}
            />
          </Form.Item>
        )}
        {kind('otherIncome', {
          icon: <PlusCircleOutlined />,
          title: 'otherIncomeSalary',
          currency: 'otherIncomeCurrency',
        })}
        {kind('suspension', {
          icon: <MinusCircleOutlined />,
          title: 'suspensionOfEarnings',
          currency: 'suspensionCurrency',
          variation: ['suspensionVariationsEnabled', 'suspensionVariations'],
        })}
        {kind('otherExpenses', {
          icon: <CarOutlined />,
          title: 'otherPaidExpenses',
          currency: 'otherExpensesCurrency',
        })}
        {kind('nonMonetaryBenefit', {
          icon: <GiftOutlined />,
          title: 'nonMonetaryBenefit',
          currency: 'nonMonetaryBenefitCurrency',
          variation: [
            'nonMonetaryBenefitVariationsEnabled',
            'nonMonetaryBenefitVariations',
          ],
        })}
      </Group>
    </div>
  );
}
